import { z } from 'zod';
import type { TenantMembershipStatus } from './tenancy.contract';

/**
 * Tenant administration: inviting members, changing their role, and
 * removing them. `roleKey` is a free string, not an enum — tenants may
 * clone and extend the seeded system-role catalog, so this contract does
 * not assume a closed set. An unrecognised key is rejected by the service,
 * not by this schema.
 */
export const inviteMemberRequestSchema = z.object({
  email: z.email().trim().toLowerCase(),
  roleKey: z.string().trim().min(1).max(100),
  /** Links the resulting membership to an existing Person, so their
   *  directory profile and their user account are the same identity —
   *  see `TenantMembership.personId`. Omitted for an ordinary invite that
   *  is not reached from a person's profile. */
  personId: z.uuid().nullish(),
});
export type InviteMemberRequest = z.infer<typeof inviteMemberRequestSchema>;

export const updateMemberRoleRequestSchema = z.object({
  roleKey: z.string().trim().min(1).max(100),
});
export type UpdateMemberRoleRequest = z.infer<typeof updateMemberRoleRequestSchema>;

export interface MemberResponse {
  readonly membershipId: string;
  readonly userId: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly isOwner: boolean;
  readonly status: TenantMembershipStatus;
  readonly roles: readonly string[];
  /** The Person this membership is linked to, if any — see `personId` on
   *  `inviteMemberRequestSchema`. */
  readonly personId: string | null;
}

/** The assignable-role catalog an invite dialog's role picker renders —
 *  every system role plus this tenant's own custom roles. */
export interface RoleSummaryResponse {
  readonly id: string;
  readonly key: string;
  readonly nameKey: string;
  /** The tenant-authored literal name, set only for a custom role — render
   *  `name ?? t(nameKey, { fallback: key })`, never `t(nameKey)` alone,
   *  since a custom role's `nameKey` is not a translation key. */
  readonly name: string | null;
  readonly isSystem: boolean;
}
